import { useEffect, useRef } from 'react';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { useAppSelector } from 'store/hook';
import DispatchMonitorAPI from 'api/dispatch-monitor.api';

export interface OperatorLiveUpdate {
  operatorName: string;
  operatorLoggedOut: boolean;
}

/**
 * Opens its own SSE connection to dispatch-monitor/live-dispatch-sse and
 * invokes `onOperatorUpdate` with the operator's login/logout state for a
 * device whenever a LOGIN/LOGOUT action arrives on the
 * `xtab:{customerCode}:last:pubsub` channel.
 *
 * Deliberately independent from HaulersMonitorView's own subscription to the
 * same endpoint — that view's SSE handler is tightly coupled to trip/KPI/
 * tonnage/log-viewer concerns unrelated to operator status, so this hook
 * opens a second, narrowly-scoped connection rather than trying to share it.
 */
export function useOperatorLiveStatus(
  onOperatorUpdate: (device: string, update: OperatorLiveUpdate) => void
) {
  const accessToken = useAppSelector((s) => s.authReducer.accessToken);
  const customerId = useAppSelector((s) => s.persistedReducer.customer.id);
  const customerCode = useAppSelector((s) => s.persistedReducer.customer.code);
  const dispatchMonitorApi = useRef(new DispatchMonitorAPI()).current;
  const esRef = useRef<InstanceType<typeof EventSourcePolyfill> | null>(null);
  const onOperatorUpdateRef = useRef(onOperatorUpdate);
  onOperatorUpdateRef.current = onOperatorUpdate;

  useEffect(() => {
    if (!accessToken || !customerId || !customerCode) return;

    const url = dispatchMonitorApi.liveDispatchSSEUrl(customerId);
    const es = new EventSourcePolyfill(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    esRef.current = es;

    es.onmessage = function (e: any) {
      try {
        const outer = JSON.parse(e.data as string) as {
          channel?: string;
          message?: string | Record<string, any>;
          sse_heartbeat?: boolean;
        };

        if (outer?.sse_heartbeat) return;
        if (outer?.channel !== `xtab:${customerCode}:last:pubsub`) return;
        if (outer?.message === undefined) return;

        const msg = (
          typeof outer.message === 'string'
            ? JSON.parse(outer.message)
            : outer.message
        ) as Record<string, any>;

        const device = msg?.device as string | undefined;
        const operatorName = msg?.operatorName as string | undefined;
        if (!device || !operatorName) return;

        if (msg?.action === 'LOGIN') {
          onOperatorUpdateRef.current(device, {
            operatorName,
            operatorLoggedOut: false,
          });
        } else if (msg?.action === 'LOGOUT') {
          onOperatorUpdateRef.current(device, {
            operatorName,
            operatorLoggedOut: true,
          });
        }
      } catch (err) {
        console.error('[operator-live-status] parse error:', err);
      }
    };

    es.onerror = () => {
      // EventSourcePolyfill auto-reconnects — no action needed
    };

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [accessToken, customerId, customerCode, dispatchMonitorApi]);
}

export default useOperatorLiveStatus;
